import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
// $FlowIgnore
import _ from 'lodash';
import {
  api,
  download,
  downloadViaAjax,
  gql,
} from 'utils/ajax';
import withRouting from 'utils/withRouting';


async function downloadExport({
  payload,
  program,
  project,
  type = 'export',
}): Promise {
  const { format, ids } = payload;
  const defaultFileName = `${type}.${format}`;

  /*
   * This is to prevent the API from returning a 400, when the `ids` is empty.
   * This triggers a download of an empty file.
   */
  if (ids.length < 1) {
    download('', format, defaultFileName);
    return false;
  }

  const finished = await downloadViaAjax(
    {
      url: `${program}/${project}/export`,
      method: 'post',
      body: JSON.stringify(payload),
    },
    format, defaultFileName,
  );
  return finished;
}

export default function canDownloadEntities(ComposedComponent) {
  class DownloadEntitiesContainer extends Component {
    static propTypes = {
      program: PropTypes.string,
      project: PropTypes.string,
      id: PropTypes.string,
      project_id: PropTypes.string,
      withLinks: PropTypes.string,
      withoutLinks: PropTypes.string,
      withPathTo: PropTypes.any,
      withoutPathTo: PropTypes.string,
      state: PropTypes.string,
    };

    downloadEntitiesByIds = async ({
      ids, format, withChildren, category,
    }): Promise => {
      const { program, project } = this.props?.router?.params;
      return downloadExport({
        program,
        project,
        payload: {
          ids: ids.join(','), format, with_children: withChildren, category,
        },
      });
    }

    downloadEntities = async ({
      entityTypes, format, withChildren, category,
    }: any): Promise => {
      const { program, project } = this.props?.router?.params;
      const { withLinks, withoutLinks, withPathTo, withoutPathTo } = this.props?.router?.location?.query;

      const { data } = await gql({
        variables: {
          projectId: project ? `${program}-${project}` : null,
          withLinks: withLinks ? withLinks.split(',') : [],
          withoutLinks: withoutLinks ? withoutLinks.split(',') : [],
          withPathTo,
          withoutPathTo,
        },
        query: `
          query EntitiesIDQuery(
            $projectId: [String]
            $withLinks: [String]
            $withoutLinks: [String]
            $withPathTo: [WithPathToInput]
            $withoutPathTo: [WithPathToInput]
          ) {
            ${entityTypes.map(
          e => `${e} ( \
                project_id: $projectId \
                with_links: $withLinks \
                without_links: $withoutLinks \
                with_path_to: $withPathTo \
                without_path_to: $withoutPathTo \
                first: 0) { id }`,
        ).join()}
          }
      `,
      });
      const ids = _.reduce(data, (acc, d) => [...acc, ...d.map(item => item.id)], []).join(',');

      return downloadExport({
        program,
        project,
        payload: {
          ids, format, with_children: withChildren, category,
        },
      });
    }

    /*
     *  Annotations
     */

    downloadAnnotations = async (format: string): Promise => {
      const { program, project } = this.props?.router?.params;

      const { data } = await gql({
        variables: {
          projectId: project ? `${program}-${project}` : null,
        },
        query: `
          query AnnotationsIdsQuery(
            $projectId: [String]
          ) {
            annotation(
              project_id: $projectId
              first: 0
            ) {
              id
            }
          }
      `,
      });
      const ids = data.annotation.map(x => x.id).join(',');

      return downloadExport({
        program,
        project,
        payload: { ids, format },
        type: 'annotations',
      });
    };

    /*
     *  Biospecimen
     */

    downloadBiospecimen = async (format: string): Promise => {
      const { program, project } = this.props?.router?.params;

      const { data } = await gql({
        variables: {
          projectId: project ? `${program}-${project}` : null,
        },
        query: `
          query BiospecimenIdsQuery(
            $projectId: [String]
          ) {
            sample(
              project_id: $projectId
              first: 0
            ) {
              id
            }
            portion(
              project_id: $projectId
              first: 0
            ) {
              id
            }
            analyte(
              project_id: $projectId
              first: 0
            ) {
              id
            }
            aliquot(
              project_id: $projectId
              first: 0
            ) {
              id
            }
          }
      `,
      });

      let ids = [
        ...data.sample.map(x => x.id),
        ...data.portion.map(x => x.id),
        ...data.analyte.map(x => x.id),
        ...data.aliquot.map(x => x.id),
      ];
      ids = ids.join(',');

      return downloadExport({
        program,
        project,
        payload: { ids, format },
        type: 'biospecimen',
      });
    };

    downloadBiospecimenByType = async (type: string, format: string): Promise => {
      const {
        program, project,
      } = this.props?.router?.params;

      const { withoutLinks, withPathTo } = this.props?.router?.location?.query;

      const { data } = await gql({
        variables: {
          projectId: project ? `${program}-${project}` : null,
          withoutLinks: withoutLinks ? withoutLinks.split(',') : [],
          withPathTo: withPathTo || null,
        },
        query: `
          query Biospecimen(
            $projectId: [String]
            $withoutLinks: [String]
            $withPathTo: [WithPathToInput]
          ) {
            ${type}(
              project_id: $projectId
              with_path_to: $withPathTo
              without_links: $withoutLinks
              first: 0
            ) {
              id
            }
          }
      `,
      });

      const ids = data[type].map(x => x.id).join(',');

      return downloadExport({
        program,
        project,
        payload: { ids, format },
        type: 'biospecimen',
      });
    };

    downloadSingleBiospecimen = (format: string): Promise => {
      const { id, project_id } = this.props;
      const [program, ...project] = project_id.split('-');

      return downloadExport({
        program,
        project: project.join('-'),
        payload: { ids: [id], format },
        type: 'biospecimen',
      });
    };

    /*
     *  Read Group
     */

    downloadSingleMetadata = async (readGroup: any, format: string): Promise => {
      const { program, project } = this.props?.router?.params;
      const { id, submitted_files: subFiles } = readGroup;
      const ids = [id].concat((subFiles || []).map(x => x.id)).join(',');

      return downloadExport({
        program,
        project,
        payload: { ids, format },
        type: 'metadata',
      });
    };

    downloadAllMetadata = async (format: string): Promise => {
      const { program, project } = this.props?.router?.params;
      const { state, withPathTo } = this.props?.router?.location?.query;

      const { data } = await gql({
        variables: {
          projectId: project ? `${program}-${project}` : null,
          state,
          withPathTo,
        },
        query: `
          query ReadGroupIdsQuery(
            $projectId: [String]
            $state: [String]
            $withPathTo: [WithPathToInput]
          ) {
            read_group(
              project_id: $projectId
              with_path_to: $withPathTo
              state: $state
              first: 0
            ) {
              id
              submitted_unaligned_reads_files(first: 0) {
                id
              }
            }
          }
      `,
      });

      const ids = [...new Set(
        data.read_group.reduce((acc, g) => ([
          ...acc,
          g.id,
          ...g.submitted_unaligned_reads_files.map(f => f.id),
        ]), []),
      )].join();

      return downloadExport({
        program,
        project,
        payload: { ids, format },
        type: 'metadata',
      });
    };

    downloadAllManifest = async (entity: string, filename: string): Promise => {
      const { program, project } = this.props?.router?.params;

      const { data } = await gql({
        variables: {
          projectId: project ? `${program}-${project}` : null,
        },
        query: `
          query ${entity}IdsQuery($projectId: [String]) {
            ${entity} (first: 0, project_id: $projectId) { id }
          }
      `,
      });

      const ids = (data[entity] || []).map(d => d.id);

      const response = await api({
        url: `${program}/${project}/manifest`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: ids
        })
      });

      const manifestString = await response.text();
      download(manifestString, 'YML', filename || 'manifest.yml');
      return !_.isUndefined(manifestString);
    };

    downloadManifestByIds = async (fileIds: Array<string>, subId, filename: string): Promise => {
      const { program, project } = this.props?.router?.params;

      const response = await api({
        url: `${program}/${project}/manifest`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
         ids: fileIds
        })
      });

      const manifestString = await response.text();
      download(manifestString, 'YML', filename || `${subId && '_'}${subId}.manifest.yml`);
      return !_.isUndefined(manifestString);
    };

    downloadSingleQCMetric = async ({ id }: any, format: string): Promise => {
      const { program, project } = this.props?.router?.params;

      return downloadExport({
        program,
        project,
        payload: { ids: [id], format },
        type: 'qcMetric',
      });
    };

    downloadAllQCMetric = async (format: string): Promise => {
      const { program, project } = this.props?.router?.params;

      const { data } = await gql({
        variables: {
          projectId: project ? `${program}-${project}` : null,
        },
        query: `
          query ReadGroupIdsQuery($projectId: [String]) {
            read_group_qc_metric(
              project_id: $projectId
              first: 0
            ) {
              id
            }
          }
      `,
      });

      const ids = data.read_group_qc_metric.map(x => x.id).join(',');

      return downloadExport({
        program,
        project,
        payload: { ids, format },
        type: 'qcMetric',
      });
    };

    render() {
      return (
        <ComposedComponent
          {...this.props}
          downloadAnnotations={this.downloadAnnotations}
          downloadBiospecimen={this.downloadBiospecimen}
          downloadBiospecimenByType={this.downloadBiospecimenByType}
          downloadSingleBiospecimen={this.downloadSingleBiospecimen}
          downloadSingleMetadata={this.downloadSingleMetadata}
          downloadAllMetadata={this.downloadAllMetadata}
          downloadManifestByIds={this.downloadManifestByIds}
          downloadAllManifest={this.downloadAllManifest}
          downloadSingleQCMetric={this.downloadSingleQCMetric}
          downloadAllQCMetric={this.downloadAllQCMetric}
          downloadEntities={this.downloadEntities}
          downloadEntitiesByIds={this.downloadEntitiesByIds}
          />
      );
    }
  }

  return connect(
    ({
      details: {
        item: {
          id,
          project_id,
        },
      },
    }) => ({
      id,
      project_id,
    }),
  )(withRouting(DownloadEntitiesContainer));
}
